import { useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import RouterLink from 'ui-addon-react-router-dom/RouterLink';
import RouterTabs from 'ui-addon-react-router-dom/RouterTabs';
import RouterTab from 'ui-addon-react-router-dom/RouterTab';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import styled from 'foundations-theming/utils';
import { MYLEARNING_URL_FRAGMENT, MY_PLAYLISTS_URL_FRAGMENT, MY_TEAM_URL_FRAGMENT, PAGE_NAMES, PARTNER_TRACKS_URL_FRAGMENT, PROFILE_URL_FRAGMENT, WATCH_LATER_URL_FRAGMENT } from '../../../constants/Navigation';
import UsageTracker from '../../../utils/UsageTracker';
import { useIsPartner } from '../../partners';
import { useNavigationState } from '../navbarQueries';
import { NavbarSavedItemsTooltipText, useShowNavbarPlaylistsTooltip, useShowNavbarWatchLaterTooltip } from '../../savedItems';
import HubSpotLogo from 'academy-common-lib/components/icon/HubSpotLogo';
import { Box } from 'foundations-components/transitional/components/Box';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TabsContainer = styled.div.withConfig({
  displayName: "NavbarTabs__TabsContainer",
  componentId: "sc-1bh0ugv-0"
})(["& a[data-tab-id],& a[data-tab-id]:hover{color:", " !important;}& [data-more-tabs]{color:", " !important;background-color:", " !important;}"], ({
  theme
}) => theme.color['text-core-on-fill-default'], ({
  theme
}) => theme.color['text-core-on-fill-default'], ({
  theme
}) => theme.color['fill-transparent-default']);
const BacklinkRouterLink = ({
  children,
  'data-onboarding': dataOnboarding,
  paddingBlock,
  paddingInline,
  pageName,
  to,
  use
}) => {
  const [hover, setHover] = useState(false);
  const {
    isActive
  } = useNavigationState(pageName);
  const handleHover = flag => () => setHover(flag);
  return /*#__PURE__*/_jsx(View, {
    as: "span",
    backgroundColor: isActive || hover ? 'do-not-use-flat-earth' : 'do-not-use-pantera',
    paddingBlock: paddingBlock,
    paddingInline: paddingInline,
    children: /*#__PURE__*/_jsx(RouterLink, {
      "data-onboarding": dataOnboarding,
      onMouseEnter: handleHover(true),
      onMouseLeave: handleHover(false),
      to: to,
      truncate: true,
      use: use,
      children: children
    })
  });
};
// data-test-id is required for the tab to be styled correctly as its used for TabId which is used by the tabs component internally to measure available space

const BacklinkRouterTab = ({
  'data-test-id': dataTestId,
  exact,
  onClick,
  pageName,
  strict,
  title,
  to
}) => {
  const [hover, setHover] = useState(false);
  const {
    backLink,
    isActive,
    hasNavigatedFromNavbarItem
  } = useNavigationState(pageName);
  const handleHover = flag => () => setHover(flag);
  return /*#__PURE__*/_jsx(RouterTab, {
    backgroundColor: isActive || hover ? 'do-not-use-flat-earth' : undefined,
    "data-test-id": dataTestId,
    exact: exact,
    onClick: onClick,
    onMouseEnter: handleHover(true),
    onMouseLeave: handleHover(false),
    strict: strict,
    tabId: dataTestId,
    title: title,
    to: hasNavigatedFromNavbarItem ? backLink : to
  });
};
const NavbarTabs = () => {
  const showNavbarWatchLaterTooltip = useShowNavbarWatchLaterTooltip();
  const showNavbarPlaylistsTooltip = useShowNavbarPlaylistsTooltip();
  const isPartner = useIsPartner();
  const onNavItemClick = trackingAction => {
    UsageTracker.track('navInteraction', {
      action: trackingAction
    });
  };
  return /*#__PURE__*/_jsxs(Flex, {
    flex: "1 1 auto",
    minInlineSize: "0",
    paddingInlineStart: "300",
    children: [/*#__PURE__*/_jsx(BacklinkRouterLink, {
      "data-onboarding": "academy-logo",
      onClick: () => onNavItemClick('hubspot-academy-clicked'),
      paddingBlock: "300",
      paddingInline: "400",
      pageName: PAGE_NAMES.CATALOG,
      to: "/",
      use: "unstyled",
      children: /*#__PURE__*/_jsx(Text, {
        as: "span",
        color: "text-core-on-fill-default",
        typography: "heading-300",
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "academyNav.heading.homepageWithLogo",
          options: {
            hubspotTextLogo: /*#__PURE__*/_jsx(HubSpotLogo, {})
          }
        })
      })
    }), /*#__PURE__*/_jsx(Box, {
      flex: "1 1 auto",
      minInlineSize: "0",
      children: /*#__PURE__*/_jsx(TabsContainer, {
        children: /*#__PURE__*/_jsxs(RouterTabs, {
          bordered: false,
          children: [/*#__PURE__*/_jsx(BacklinkRouterTab, {
            "data-test-id": "nav-tab-learnings",
            exact: false,
            onClick: () => onNavItemClick('my-learning-clicked'),
            pageName: PAGE_NAMES.MY_LEARNING,
            strict: false,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyNav.heading.myLearning"
            }),
            to: MYLEARNING_URL_FRAGMENT
          }), /*#__PURE__*/_jsx(BacklinkRouterTab, {
            "data-test-id": "nav-tab-watch-later",
            exact: false,
            onClick: () => onNavItemClick('watch-later-clicked'),
            pageName: PAGE_NAMES.WATCH_LATER,
            strict: false,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyNav.heading.watchLater"
            }),
            to: WATCH_LATER_URL_FRAGMENT,
            tooltip: /*#__PURE__*/_jsx(NavbarSavedItemsTooltipText, {}),
            tooltipProps: {
              open: showNavbarWatchLaterTooltip
            }
          }), /*#__PURE__*/_jsx(BacklinkRouterTab, {
            "data-test-id": "nav-tab-my-playlists",
            exact: false,
            onClick: () => onNavItemClick('my-playlists-clicked'),
            pageName: PAGE_NAMES.PLAYLISTS,
            strict: false,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyNav.heading.myPlaylists"
            }),
            to: MY_PLAYLISTS_URL_FRAGMENT,
            tooltip: /*#__PURE__*/_jsx(NavbarSavedItemsTooltipText, {}),
            tooltipProps: {
              maxInlineSize: '285px',
              open: showNavbarPlaylistsTooltip
            }
          }), /*#__PURE__*/_jsx(BacklinkRouterTab, {
            "data-test-id": "nav-tab-my-profile",
            exact: false,
            onClick: () => onNavItemClick('my-profile-clicked'),
            pageName: PAGE_NAMES.MY_PROFILE,
            strict: false,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyNav.heading.myProfile"
            }),
            to: PROFILE_URL_FRAGMENT
          }), /*#__PURE__*/_jsx(BacklinkRouterTab, {
            "data-test-id": "nav-tab-my-team",
            exact: false,
            onClick: () => onNavItemClick('my-team-clicked'),
            pageName: PAGE_NAMES.MY_TEAM,
            strict: false,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyNav.heading.myTeam"
            }),
            to: MY_TEAM_URL_FRAGMENT
          }), isPartner && /*#__PURE__*/_jsx(BacklinkRouterTab, {
            "data-test-id": "nav-tab-partners",
            exact: false,
            onClick: () => onNavItemClick('partner-clicked'),
            pageName: PAGE_NAMES.PARTNER,
            strict: false,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "academyNav.heading.partners"
            }),
            to: PARTNER_TRACKS_URL_FRAGMENT
          })]
        })
      })
    })]
  });
};
export default NavbarTabs;